import { FC, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getResult } from 'store/player/playerAsync';
// Selectors
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectResult, selectLeaderboard } from 'store/player/playerSelectors';
// Models
import IQuiz from 'models/Quiz';
// Types
import QuizTypes from 'types/QuizTypes';
// Components
import AppBar from 'components/AppBar';
import PlayerResult from 'components/PlayerResult';
import TextCountdown from 'components/TextCountdown';
import SocialShare from 'components/SocialShare';
import { Button } from 'components/Buttons';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Components
import PublicDevicePlayable from 'components/PublicDevicePlayable';
// Icons
import { FiRefreshCcw } from 'react-icons/fi';
// Utilities
import { buildUserName } from 'utilities/Utilities';
// i18next
import { useTranslation } from 'react-i18next';

const LeaderboardPage:FC = () => {
  const { t } = useTranslation('common');
  const { slug } = useParams<{ slug:string }>();

  const dispatch = useAppDispatch();
  const navigateTo = useNavigateTo();

  const quiz:IQuiz = useAppSelector(selectQuiz);
  const result:any = useAppSelector(selectResult);
  const leaderboard:any[] = useAppSelector(selectLeaderboard);

  const handleGetResult = () => {
    dispatch(getResult({ quizName: slug, limit: 96 }))
  }

  const quizEnded = dayjs().isAfter(+result.quiz.endsDate);
  const { numberOfQuestions, numberOfAnsweredQuestions } = result.quiz;

  return (
    <Fragment>
      <PublicDevicePlayable />
      <AppBar  />
      <div className="grow p-4 overflow-y-auto">
        <div className="flex items-center mb-4">
          <div style={{ width: 72 }}>
            <Button
              onClick={() => navigateTo('post-quiz')}
              size="small"
            >{t('common.back')}</Button>
          </div>
          <div className="grow flex justify-center items-center gap-3">
            <h2 className="font-bold text-center text-lg">{t('common.leaderboard')}</h2>
            {quiz.type === QuizTypes.Prediction && numberOfQuestions !== numberOfAnsweredQuestions && (
              <Button onClick={handleGetResult} size="small">
                <FiRefreshCcw />
              </Button>
            )}
          </div>
          <div style={{ width: 72 }}></div>
        </div>
        {quiz.type === QuizTypes.Prediction && (
          quizEnded ? (
            <p className="text-center text-sm mb-4">
              {numberOfQuestions === numberOfAnsweredQuestions
                ? t('quiz.leaderboardPage.finalResults')
                : t('quiz.leaderboardPage.answeredQuestions', { questions: numberOfQuestions, answeredQuestions: numberOfAnsweredQuestions })
              }
            </p>
          ) : (
            <p className="text-center text-xs mb-4">
              {t('quiz.leaderboardPage.leaderboardPredictionHint')}
            </p>
          )
        )}
        {[QuizTypes.Personality, QuizTypes.Scoring].includes(quiz.type) && (
          <TextCountdown startsDate={quiz.startsDate} endsDate={quiz.endsDate} />
        )}
        {result?.quiz?.quizGroup?.slug ? (
          <div className="text-center mb-4">
            <Button
              className="capitalize"
              onClick={() => {
                navigateTo('quiz-group', {}, result.quiz.quizGroup.slug)
              }}
              disabled={!result}
            >{t('quiz.leaderboardPage.leagueDetails')}</Button>
          </div>
        ) : null}
        {quiz.socialShareEnabled && (quiz.hashtags || quiz.socialShareURL || quiz.socialShareImage) ? (
          <div className="mb-4">
            {!!quiz.socialShareBonusValue && quiz.type !== QuizTypes.Poll && (
              <h3 className="text-center text-sm text-blue-500 font-bold mb-2 px-4">{t('quiz.leaderboardPage.earnAdditionalPoints', { points: quiz.socialShareBonusValue })}</h3>
            )}
            <SocialShare />
          </div>
        ) : null}
        {leaderboard.length ? (
          <div className="shadow-neu-content-inner rounded-lg p-2">
            <ul className="rounded">
              {leaderboard.map((result:any, index:number) => (
                <li
                  key={`leaderboard-item-${index}`}
                  className="flex py-2 px-3 border-b border-gray-100 last:border-none"
                >
                  <div className="flex-1 flex items-center">
                    <span className="font-bold mr-2">{index + 1}.</span>
                    {buildUserName(result)}
                  </div>
                  <div className="font-bold">{result.score}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      <PlayerResult />
    </Fragment>
  )
}

export default LeaderboardPage;
